import { XAPIRunner } from 'libs-frontend-xapi/dist/js/xapirunner.js';
import { XAPIAnalytics } from 'libs-frontend-xapi/dist/js/xapianalytics.js';
import { Injectable } from '@angular/core';
import { ToolsService } from './tools.service';
import Raven from 'raven-js';
import { environment } from 'environments/environment';
import { CustomPubSub } from 'modules/app/services/custom-pubsub.service';
import { ApiGatewayHelperService } from 'modules/app/services/apigateway-helper.service';

@Injectable()
export class XAPIHelperService extends CustomPubSub {
    private xapiLO: any;
    private xapiLOAnalytics: any;

    private unsavedDataDocIdMap = {};

    // angular component ref of toaster
    // this toaster will be shown on data saving error
    private _toasterReference;

    // array to maintain the document ids for which we have sent a postXAPIStatement call
    private _callsSentToXAPI = [];

    // variable to maintain a count specifying that how many times the postStatement call has CONTINUOUSLY failed
    xAPIPostStatementsFailureCount = 0;
    
    constructor(
        public toolsService: ToolsService,
        public apiGatewayHelperService: ApiGatewayHelperService,
    ) {
        super();
        this.xAPIAnalyticsSuccessHandler = this.xAPIAnalyticsSuccessHandler.bind(this);
        this.xAPIAnalyticsFailureHandler = this.xAPIAnalyticsFailureHandler.bind(this);
        this.xAPIPostedCallbackHandler = this.xAPIPostedCallbackHandler.bind(this);
        this.xAPIUnsavedDataHandler = this.xAPIUnsavedDataHandler.bind(this);

        // only the registered events can be subscribed and are fired by the classes extending custom pubsub
        this.registerEvents(['success']);
        
    }

    getPendingXPICalls(){
        // console.log("getPendingXPICalls: ", this._callsSentToXAPI);
        return this._callsSentToXAPI;
    }

    /**
     * method to be called from student book
     * for setting the ref of toaster angular comp in this service
     */
    public setToasterReference(reference) {
        this._toasterReference = reference;
    }

    setXAPIRunnerObject(initParams, eventCallbacks, options: any = {}) {
        let xapiLO = new XAPIRunner(initParams, eventCallbacks);
        
        // add comment here
        if (options.returnInstance) {
            return xapiLO;
        } else {
            this.xapiLO = xapiLO;
        }
    }

    setXAPIAnalyticsObject(analyticsDataObj, options: any = {}) {

        let params = {
            serverRoutePrefix: analyticsDataObj.serverRoutePrefix,
            productCode: analyticsDataObj.dlsProductCode,
            spaceId: analyticsDataObj.spaceId,
            classId: analyticsDataObj.dlsClassId,
            assignedPathId: analyticsDataObj.dlsAssignmentId,
            userDetails: { extUserId: analyticsDataObj.userId },
            getQueryParamsObj: this.getxAPIQueryParams.bind(this),
            isCBPClass: analyticsDataObj.isCBPClass
        };

        let eventHandlers = options.eventHandlers;
        if (!eventHandlers) {
            eventHandlers = {
                successCallback: this.xAPIAnalyticsSuccessHandler,
                errorCallback: this.xAPIAnalyticsFailureHandler,
                postedCallback: this.xAPIPostedCallbackHandler,
                previousStatementCallback: this.xAPIUnsavedDataHandler,
            }
        }

        if (analyticsDataObj.persistentState) {
            params['persistentState'] = analyticsDataObj.persistentState;
        }

        params = { ...params, ...eventHandlers };

        let xapiLOAnalytics = new XAPIAnalytics(params);

        // add comment here
        if (options.returnInstance) {
            return xapiLOAnalytics;
        } else {
            this.xapiLOAnalytics = xapiLOAnalytics;
        }

    }

    getxAPIQueryParams() {
        if(environment.isDesktopApp) {
            return this.apiGatewayHelperService.getAPIAuthToken();
        }else{
            return Promise.resolve({});
        }
    }

    xAPIAnalyticsSuccessHandler(source, data){
        if (source !== 'postStatements') {
            return;
        }

        this.xAPIPostStatementsFailureCount = 0;
        
        this.toolsService.updateToolbarConfig({key:'toolbarStatusType', value:3});

        if(data && data.entities && data.entities.forEach){
            data.entities.forEach(entity => {
                if(entity && entity["user_data"]){
                    let docId = entity["user_data"]["documentid"];
                    let docIdIdx = this._callsSentToXAPI.indexOf(docId);
                    if(docId && (docIdIdx != -1)){
                        this._callsSentToXAPI.splice(docIdIdx, 1);
                    }
                }
            })
        }
        

        this.publish('success', { source, data });
        
    }

    xAPIAnalyticsFailureHandler(errorSource, data){
        this.handleXAPIAnalyticsError(errorSource, data);
        if (errorSource !== 'postStatements') {
            return;
        }
        this.xAPIPostStatementsFailureCount++;

        if(this.xAPIPostStatementsFailureCount >= 3){
            this.toolsService.updateToolbarConfig({key:'toolbarStatusType', value:1});

            this.xAPIPostStatementsFailureCount = 0;
            
            this._toasterReference.showToast({ type: "save-data-error" });
        }
    }

    xAPIPostedCallbackHandler(source, data) {
        if (source !== 'postStatements') {
            return;
        }
        this.toolsService.updateToolbarConfig({key:'toolbarStatusType', value:2});
    }

    /**
     * method to receive the unsavde data from the xAPI so that it can be shown to the user
     * @param data: schema is same as that sent to the DLS
     */
    xAPIUnsavedDataHandler(data){
        this.unsavedDataDocIdMap = {};

        if(data && data.entities){
            data.entities.forEach(el => {
                this.unsavedDataDocIdMap[el['item-code']] = Object.assign(el['user_data'], {created: el.timestamp || Date.now()});
            });
        }

    }

    getUnsavedDataByDocumentId(docId){
        let res = this.unsavedDataDocIdMap[docId];
        delete this.unsavedDataDocIdMap[docId];
        return res;
    }

    getUnsavedDataByFolderId(folderid, userId?){
        // stores data nodes which will be deleted from unsaved data 
        // once they have been provided to the drawing/ notes data service
        let dataNodesToBeDeleted = [];
        
        let result = [];
        
        for (const docId in this.unsavedDataDocIdMap) {

            let node = this.unsavedDataDocIdMap[docId];
            if(node.folderid.indexOf(folderid) == 0){
                dataNodesToBeDeleted.push(docId);
                result.push(node);
            }
        }

        dataNodesToBeDeleted.forEach(docId => {
            delete this.unsavedDataDocIdMap[docId];
        })

        return result;
    }

    getXAPIRunnerObject() {
        return this.xapiLO;
    }

    getXAPIAnalyticsObject() {
        return this.xapiLOAnalytics;
    }

    postStatmentToXAPI(uniqueId, statement){
        this._callsSentToXAPI.push(uniqueId);
        this.getXAPIAnalyticsObject().postStatements(statement);
    }

    async flushStateAndStatement(){
        // flushing xAPI statements from local storage
        await this.getXAPIAnalyticsObject().flushStateAndStatements(true, {notifyViaCallback: true});
    }

    public handleXAPIAnalyticsError(errorSource, error) {
        const message = `[Custom] captured failed XAPIRunner http request for ${errorSource} with status ${error.status}...`;
        Raven.captureBreadcrumb({
            message,
            category: 'xhr',
            data: error,
            level: 'error'
        });
        Raven.captureException(message);
    }

}